import { useState, useCallback } from 'react';
import { enumCategorieRecherche } from '../enums/enumCategorieRecherche.js';
import EtudiantService from '../services/etudiantService.js';
import AppellationService from '../services/appellationService.js';
import { useTranslation } from 'react-i18next';

export const useProfileFilters = (currentUser, adminImpersonatingEtudiantId) => {
    const { t } = useTranslation();
    const [profileFiltersLoading, setProfileFiltersLoading] = useState(false);
    const etudiantService = new EtudiantService();
    const appellationService = new AppellationService();

    const buildContractFilters = (paireContrats) => {
        const defaultFilters = [];
        const addedContractCategories = new Set();
        
        paireContrats?.forEach(contrat => {
            if (contrat?.code) {
                let cat, label;
                switch (contrat.code) {
                    case 'STA':
                        cat = enumCategorieRecherche.ContratStage;
                        label = t('search.stage');
                        break;
                    case 'ALTCA':
                        cat = enumCategorieRecherche.ContratAltApp;
                        label = t('search.alt_app');
                        break;
                    case 'ALTCP':
                        cat = enumCategorieRecherche.ContratAltPro;
                        label = t('search.alt_pro');
                        break;
                    case 'CDD':
                    case 'CDI':
                    case 'CDIINTERIM':
                        cat = enumCategorieRecherche.ContratCDDCDI;
                        label = t('search.cdd_cdi');
                        break;
                    default:
                        cat = enumCategorieRecherche.ContratAutres;
                        label = t('search.others');
                }
                
                if (!addedContractCategories.has(cat)) {
                    defaultFilters.push({ categorie: cat, value: true, label });
                    addedContractCategories.add(cat);
                }
            }
        });
        
        return defaultFilters;
    };

    const buildAppellationFilters = async (etudiantAppellations, etudiantId) => {
        const defaultFilters = [];
        
        if (!Array.isArray(etudiantAppellations) || etudiantAppellations.length === 0) {
            return defaultFilters;
        }

        try {
            const allowedAppellations = await appellationService.getAllowedAppellationsForEtudiant(etudiantId);
            
            if (Array.isArray(allowedAppellations) && allowedAppellations.length > 0) {
                const selectedAppellations = allowedAppellations.filter(appellation => 
                    appellation?.id &&
                    etudiantAppellations.some(etudApp => 
                        etudApp?.appellationId === appellation.id && etudApp.prisEnCompte === true
                    )
                );

                selectedAppellations.forEach(appellation => {
                    if (appellation.libelle) {
                        defaultFilters.push({
                            categorie: enumCategorieRecherche.Appellation,
                            value: String(appellation.id),
                            label: appellation.libelle,
                            type: null
                        });
                    }
                });
            }
        } catch (error) {
            etudiantAppellations.forEach(appellation => {
                if (appellation?.prisEnCompte && appellation.appellationId) {
                    defaultFilters.push({
                        categorie: enumCategorieRecherche.Appellation,
                        value: String(appellation.appellationId),
                        label: `Emploi ${appellation.appellationId}`,
                        type: null
                    });
                }
            });
        }
        
        return defaultFilters;
    };

    const buildFormationFilters = (formationSuivie, formationEnCours, formationAVenir) => {
        const defaultFilters = [];
        const allFormations = [
            ...(Array.isArray(formationSuivie) ? formationSuivie : []),
            ...(Array.isArray(formationEnCours) ? formationEnCours : []),
            ...(Array.isArray(formationAVenir) ? formationAVenir : [])
        ];

        if (allFormations.length === 0) return defaultFilters;

        const domainesSet = new Set();
        const metiersSet = new Set();
        const appellationsSet = new Set();

        allFormations.forEach(formation => {
            if (!formation) return;

            formation.dipDomaines?.forEach(domaine => {
                if (domaine?.id && domaine.domaineType && !domainesSet.has(domaine.id)) {
                    domainesSet.add(domaine.id);
                    defaultFilters.push({
                        categorie: enumCategorieRecherche.DipDomaine,
                        value: domaine.id,
                        label: domaine.domaineType,
                        type: null
                    });
                }
            });

            formation.dipMetiers?.forEach(metier => {
                if (metier?.id && metier.metierType && !metiersSet.has(metier.id)) {
                    metiersSet.add(metier.id);
                    defaultFilters.push({
                        categorie: enumCategorieRecherche.DipMetier,
                        value: metier.id,
                        label: metier.metierType,
                        type: null
                    });
                }
            });

            formation.appellations?.forEach(appellation => {
                if (appellation?.id && appellation.label && !appellationsSet.has(appellation.id)) {
                    appellationsSet.add(appellation.id);
                    defaultFilters.push({
                        categorie: enumCategorieRecherche.Appellation,
                        value: String(appellation.id),
                        label: appellation.label,
                        type: null
                    });
                }
            });
        });

        return defaultFilters;
    };

    const buildMobilityFilters = (profileData) => {
        const defaultFilters = [];

        if (profileData.mobiliteInternationale === true) {
            defaultFilters.push({
                categorie: enumCategorieRecherche.Pays,
                value: 'international',
                label: 'International',
                type: null
            });
        }

        if (profileData.mobiliteFrancaise === true) {
            defaultFilters.push({
                categorie: enumCategorieRecherche.Pays,
                value: 'france',
                label: 'France',
                type: null
            });
        }

        profileData.mobiliteRegion?.forEach(region => {
            if (region?.id) {
                defaultFilters.push({
                    categorie: enumCategorieRecherche.Region,
                    value: String(region.id),
                    label: region.libelle,
                    type: null
                });
            }
        });

        profileData.mobiliteDepartement?.forEach(departement => {
            if (departement?.id && departement.libelle) {
                defaultFilters.push({
                    categorie: enumCategorieRecherche.Departement,
                    value: String(departement.id),
                    label: departement.libelle,
                    type: null
                });
            }
        });

        profileData.mobiliteVille?.forEach(ville => {
            if (ville?.code) {
                defaultFilters.push({
                    categorie: enumCategorieRecherche.Ville,
                    value: String(ville.code),
                    label: ville.libelle,
                    type: null
                });
            }
        });

        return defaultFilters;
    };

    const loadStudentProfileFilters = useCallback(async () => {
        setProfileFiltersLoading(true);
        try {
            let profileData = {};
            
            if (adminImpersonatingEtudiantId || (currentUser?.user?.id)) {
                const etudiantId = adminImpersonatingEtudiantId || currentUser.user.id;
                const [situationPro, etudes] = await Promise.all([
                    etudiantService.getEtudiantSituationPro(adminImpersonatingEtudiantId),
                    etudiantService.getEtudiantEtudes(adminImpersonatingEtudiantId)
                ]);
                
                profileData = {
                    ...situationPro,
                    formationSuivie: etudes.formationSuivie,
                    formationEnCours: etudes.formationEnCours,
                    formationAVenir: etudes.formationAVenir
                };
            }
            
            if (profileData && Object.keys(profileData).length > 0) {
                let defaultFilters = [];

                // Build all filter types
                defaultFilters.push(...buildContractFilters(profileData.paireContrats));
                
                const appellationFilters = await buildAppellationFilters(
                    profileData.etudiantAppellations, 
                    adminImpersonatingEtudiantId || currentUser.user.id
                );
                defaultFilters.push(...appellationFilters);
                
                defaultFilters.push(...buildFormationFilters(
                    profileData.formationSuivie,
                    profileData.formationEnCours,
                    profileData.formationAVenir
                ));
                
                defaultFilters.push(...buildMobilityFilters(profileData));

                return defaultFilters;
            }
            
            return [];
        } catch (error) {
            console.error('Error loading profile filters:', error);
            return [];
        } finally {
            setProfileFiltersLoading(false);
        }
    }, [currentUser, adminImpersonatingEtudiantId]);

    return {
        profileFiltersLoading,
        loadStudentProfileFilters
    };
};
